/**
 * WebSocket Context Provider
 *
 * The SOLE WebSocket connection manager for the CGIC TransferHub application.
 * Handles connection lifecycle, exponential backoff reconnection, and
 * topic-based broadcast message routing.
 *
 * Backend message format:
 *   {"type":"broadcast","topic":"partner_status_changes","data":{...},"timestamp":"..."}
 *
 * CGIC TransferHub - WebSocket Real-time Updates
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useState,
  ReactNode,
} from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  WebSocketStatus,
  WebSocketState,
  WebSocketContextValue,
  ReconnectionConfig,
  DEFAULT_RECONNECTION_CONFIG,
  TopicCallback,
  isBroadcastMessage,
  isHeartbeatMessage,
} from '../types/websocket';

// ============================================================================
// Types
// ============================================================================

/** Props for the WebSocket provider component */
export interface WebSocketProviderProps {
  /** Child components */
  children: ReactNode;
  /** WebSocket URL - defaults to the VITE_WEBSOCKET_URL environment variable */
  url?: string;
  /** Custom reconnection configuration */
  reconnectionConfig?: Partial<ReconnectionConfig>;
  /** Whether to auto-connect on mount (default: true) */
  autoConnect?: boolean;
}

/** Internal subscription entry */
interface TopicSubscription {
  topic: string;
  callback: TopicCallback;
}

// ============================================================================
// Context
// ============================================================================

const WebSocketContext = createContext<WebSocketContextValue | undefined>(undefined);

// ============================================================================
// Provider Component
// ============================================================================

/**
 * WebSocket Provider Component
 *
 * Manages a SINGLE WebSocket connection for the entire app.
 * Handles the backend's broadcast message format and routes data
 * to topic-based subscribers.
 *
 * Features:
 * - Connection establishment and cleanup
 * - Exponential backoff reconnection (1s initial, 8s max, 2x multiplier)
 * - Topic-based subscription dispatch
 * - Sends subscribe/unsubscribe messages to the server
 * - Re-establishes subscriptions after reconnect
 * - Ignores heartbeat/pong messages
 *
 * @example
 * ```tsx
 * <WebSocketProvider url="wss://api.example.com/ws">
 *   <App />
 * </WebSocketProvider>
 * ```
 */
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
  url = import.meta.env.VITE_WEBSOCKET_URL,
  reconnectionConfig: customConfig,
  autoConnect = true,
}) => {
  // Merge custom config with defaults
  const reconnectionConfig: ReconnectionConfig = {
    ...DEFAULT_RECONNECTION_CONFIG,
    ...customConfig,
  };

  // ============================================================================
  // State
  // ============================================================================

  const [connectionState, setConnectionState] = useState<WebSocketState>({
    status: 'disconnected',
    reconnectAttempts: 0,
    lastConnectedAt: null,
    lastDisconnectedAt: null,
  });

  // ============================================================================
  // Refs (persist across re-renders)
  // ============================================================================

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isMountedRef = useRef(true);
  // THUB-298: breaks the connect <-> scheduleReconnect cycle.
  //
  // connect() needs to schedule a reconnect on failure, and scheduleReconnect() needs to call
  // connect() when the timer fires. Referencing each other directly meant connect used
  // scheduleReconnect before its declaration (react-hooks/immutability) and the dependency was
  // suppressed with an eslint-disable. Routing one direction through a ref removes the cycle:
  // connect depends on nothing new, and scheduleReconnect can keep connect as a real dependency.
  const scheduleReconnectRef = useRef<() => void>(() => {});

  // Topic-based subscriptions
  const subscriptionsRef = useRef<Set<TopicSubscription>>(new Set());

  // Track active topic names for re-subscription after reconnect
  const activeTopicsRef = useRef<Set<string>>(new Set());

  // ============================================================================
  // Helper Functions
  // ============================================================================

  const calculateBackoffDelay = useCallback((attempt: number): number => {
    const delay = reconnectionConfig.initialDelay * Math.pow(reconnectionConfig.backoffMultiplier, attempt);
    return Math.min(delay, reconnectionConfig.maxDelay);
  }, [reconnectionConfig.initialDelay, reconnectionConfig.backoffMultiplier, reconnectionConfig.maxDelay]);

  const updateStatus = useCallback((status: WebSocketStatus, additionalState?: Partial<WebSocketState>) => {
    if (!isMountedRef.current) return;
    setConnectionState(prev => ({
      ...prev,
      status,
      ...additionalState,
    }));
  }, []);

  /**
   * Dispatch broadcast data to all matching topic subscribers
   */
  const dispatchBroadcast = useCallback((topic: string, data: any) => {
    subscriptionsRef.current.forEach(subscription => {
      if (subscription.topic === topic) {
        try {
          subscription.callback(data);
        } catch (error) {
          console.error('[WebSocket] Error in subscription callback for topic', topic, ':', error);
        }
      }
    });
  }, []);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  /**
   * Start keepalive ping to prevent API Gateway idle timeout (10 min default).
   * Sends a ping every 4 minutes to keep the connection and DynamoDB record alive.
   */
  const startKeepalive = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    pingIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 4 * 60 * 1000); // 4 minutes
  }, []);

  const stopKeepalive = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  /**
   * Send a JSON message to the server (if connected)
   */
  const sendMessage = useCallback((message: object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  /**
   * Send subscribe message to server for given topics
   */
  const sendSubscribeToServer = useCallback((topics: string[]) => {
    if (topics.length > 0) {
      sendMessage({ type: 'subscribe', topics });
    }
  }, [sendMessage]);

  /**
   * Send unsubscribe message to server for given topics
   */
  const sendUnsubscribeToServer = useCallback((topics: string[]) => {
    if (topics.length > 0) {
      sendMessage({ type: 'unsubscribe', topics });
    }
  }, [sendMessage]);

  // ============================================================================
  // Connection Management
  // ============================================================================

  const connect = useCallback(async () => {
    if (!url) {
      console.warn('[WebSocket] No WebSocket URL configured.');
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    updateStatus('connecting');

    // Fetch Cognito token for WebSocket authentication
    let wsUrl = url;
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (token) {
        wsUrl = `${url}?token=${token}`;
      }
    } catch (authError) {
      console.warn('[WebSocket] Could not fetch auth token, connecting without authentication:', authError);
    }

    console.log('[WebSocket] Connecting to:', url);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!isMountedRef.current) return;

        console.log('[WebSocket] Connection established');
        reconnectAttemptsRef.current = 0;

        updateStatus('connected', {
          reconnectAttempts: 0,
          lastConnectedAt: new Date(),
        });

        // Start keepalive ping to prevent API Gateway idle timeout
        startKeepalive();

        // Re-establish topic subscriptions after reconnect
        const topics = Array.from(activeTopicsRef.current);
        if (topics.length > 0) {
          sendSubscribeToServer(topics);
        }
      };

      ws.onmessage = (event: MessageEvent) => {
        if (!isMountedRef.current) return;

        try {
          const data = JSON.parse(event.data);

          // Ignore heartbeat/pong messages
          if (isHeartbeatMessage(data)) {
            return;
          }

          // Handle broadcast messages
          if (isBroadcastMessage(data)) {
            dispatchBroadcast(data.topic, data.data);
            return;
          }

          // Log unrecognized message types for debugging
          console.debug('[WebSocket] Unrecognized message type:', data.type, data);
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
        }
      };

      ws.onclose = (event: CloseEvent) => {
        if (!isMountedRef.current) return;

        console.log('[WebSocket] Connection closed:', event.code, event.reason);
        wsRef.current = null;
        stopKeepalive();

        updateStatus('disconnected', {
          lastDisconnectedAt: new Date(),
        });

        // Attempt reconnection if not a clean close and within max attempts
        if (event.code !== 1000 && reconnectAttemptsRef.current < reconnectionConfig.maxAttempts) {
          scheduleReconnectRef.current();
        }
      };

      ws.onerror = (error: Event) => {
        if (!isMountedRef.current) return;
        console.error('[WebSocket] Connection error:', error);
      };

    } catch (error) {
      console.error('[WebSocket] Failed to create WebSocket:', error);
      updateStatus('disconnected');
      scheduleReconnectRef.current();
    }
  }, [url, updateStatus, dispatchBroadcast, reconnectionConfig.maxAttempts, sendSubscribeToServer, startKeepalive, stopKeepalive]);

  const scheduleReconnect = useCallback(() => {
    if (!isMountedRef.current) return;
    if (reconnectAttemptsRef.current >= reconnectionConfig.maxAttempts) {
      console.log('[WebSocket] Max reconnection attempts reached');
      return;
    }

    clearReconnectTimeout();

    const delay = calculateBackoffDelay(reconnectAttemptsRef.current);
    reconnectAttemptsRef.current += 1;

    console.log(`[WebSocket] Scheduling reconnect attempt ${reconnectAttemptsRef.current} in ${delay}ms`);

    updateStatus('reconnecting', {
      reconnectAttempts: reconnectAttemptsRef.current,
    });

    reconnectTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        connect();
      }
    }, delay);
  }, [clearReconnectTimeout, calculateBackoffDelay, updateStatus, connect, reconnectionConfig.maxAttempts]);

  // Published after commit, not during render (react-hooks/refs). connect() reads this ref, so
  // it always invokes the current scheduleReconnect without depending on its identity.
  useEffect(() => {
    scheduleReconnectRef.current = scheduleReconnect;
  }, [scheduleReconnect]);

  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    stopKeepalive();
    reconnectAttemptsRef.current = 0;

    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }

    updateStatus('disconnected');
  }, [clearReconnectTimeout, stopKeepalive, updateStatus]);

  const reconnect = useCallback(() => {
    console.log('[WebSocket] Manual reconnection triggered');
    disconnect();
    reconnectAttemptsRef.current = 0;
    connect();
  }, [disconnect, connect]);

  // ============================================================================
  // Topic Subscription Management
  // ============================================================================

  /**
   * Subscribe to a broadcast topic.
   * When a message arrives with {"type":"broadcast","topic":"partner_status_changes","data":{...}},
   * the callback receives the `data` object.
   *
   * Returns an unsubscribe function.
   */
  const subscribe = useCallback((topic: string, callback: TopicCallback): (() => void) => {
    const subscription: TopicSubscription = { topic, callback };
    subscriptionsRef.current.add(subscription);

    // Track this topic and tell the server
    const isNewTopic = !activeTopicsRef.current.has(topic);
    activeTopicsRef.current.add(topic);

    if (isNewTopic) {
      sendSubscribeToServer([topic]);
    }

    // Return unsubscribe function
    return () => {
      subscriptionsRef.current.delete(subscription);

      // Check if any other subscriptions exist for this topic
      let topicStillActive = false;
      subscriptionsRef.current.forEach(sub => {
        if (sub.topic === topic) topicStillActive = true;
      });

      if (!topicStillActive) {
        activeTopicsRef.current.delete(topic);
        sendUnsubscribeToServer([topic]);
      }
    };
  }, [sendSubscribeToServer, sendUnsubscribeToServer]);

  // ============================================================================
  // Lifecycle
  // ============================================================================

  useEffect(() => {
    isMountedRef.current = true;

    // Close WebSocket cleanly on page refresh/navigation so $disconnect fires
    // and the server-side DynamoDB record is cleaned up immediately
    const handleBeforeUnload = () => {
      stopKeepalive();
      if (wsRef.current) {
        wsRef.current.close(1000, 'Page unload');
        wsRef.current = null;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    if (autoConnect) {
      connect();
    }

    // Capture refs for cleanup (refs may change before unmount)
    const subscriptions = subscriptionsRef.current;
    const activeTopics = activeTopicsRef.current;

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearReconnectTimeout();
      stopKeepalive();

      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmount');
        wsRef.current = null;
      }

      subscriptions.clear();
      activeTopics.clear();
    };
  }, [autoConnect, connect, clearReconnectTimeout, stopKeepalive]);

  // Reconnect when URL changes (skip initial mount — autoConnect handles that)
  const isInitialMountRef = useRef(true);
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    if (url && connectionState.status !== 'connecting') {
      reconnect();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const contextValue: WebSocketContextValue = {
    isConnected: connectionState.status === 'connected',
    connectionStatus: connectionState.status,
    reconnectAttempts: connectionState.reconnectAttempts,
    subscribe,
    reconnect,
    disconnect,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access WebSocket context
 *
 * Must be used within a WebSocketProvider
 *
 * @example
 * ```tsx
 * const { isConnected, subscribe } = useWebSocket();
 *
 * useEffect(() => {
 *   const unsubscribe = subscribe('partner_status_changes', (data) => {
 *     console.log('Partner update:', data);
 *   });
 *   return unsubscribe;
 * }, [subscribe]);
 * ```
 */
export const useWebSocket = (): WebSocketContextValue => {
  const context = useContext(WebSocketContext);

  if (context === undefined) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }

  return context;
};

/**
 * Like `useWebSocket`, but returns `undefined` instead of throwing when no provider is mounted.
 *
 * For components where live refresh is an ENHANCEMENT rather than a requirement (THUB-182 wired
 * the Connection and Partner detail modals to subscribe directly, because they hold their data
 * in component-local state that no query invalidation can reach). Those modals must still render
 * without a provider — they are rendered as children from list views, and a missing provider
 * should cost you live updates, not blank the panel with a thrown error.
 *
 * Callers must handle `undefined` and degrade to their existing manual-refresh path.
 */
export const useOptionalWebSocket = (): WebSocketContextValue | undefined =>
  useContext(WebSocketContext);

// ============================================================================
// Connection Status Component
// ============================================================================

/** Props for the connection status indicator */
interface ConnectionStatusIndicatorProps {
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the status text */
  showText?: boolean;
}

/**
 * Connection Status Indicator Component
 *
 * Displays current WebSocket connection status with CGIC brand-colored indicators:
 * - Connected: Success green with soft pulse animation
 * - Disconnected: Danger red (static)
 * - Connecting/Reconnecting: Warning amber with pulse animation
 */
export const ConnectionStatusIndicator: React.FC<ConnectionStatusIndicatorProps> = ({
  className = '',
  showText = false,
}) => {
  const { connectionStatus, reconnectAttempts } = useWebSocket();

  const statusConfig: Record<WebSocketStatus, {
    dotClass: string;
    text: string;
    textClass: string;
  }> = {
    connected: {
      dotClass: 'connection-dot-connected',
      text: 'Connected',
      textClass: 'text-success-600 dark:text-success-400',
    },
    disconnected: {
      dotClass: 'connection-dot-disconnected',
      text: 'Disconnected',
      textClass: 'text-danger-600 dark:text-danger-400',
    },
    connecting: {
      dotClass: 'connection-dot-reconnecting',
      text: 'Connecting...',
      textClass: 'text-warning-600 dark:text-warning-400',
    },
    reconnecting: {
      dotClass: 'connection-dot-reconnecting',
      text: `Reconnecting (${reconnectAttempts})...`,
      textClass: 'text-warning-600 dark:text-warning-400',
    },
  };

  const config = statusConfig[connectionStatus];

  return (
    <div
      className={`connection-indicator ${className}`}
      role="status"
      aria-live="polite"
      aria-label={`WebSocket connection status: ${config.text}`}
    >
      <span className={config.dotClass} aria-hidden="true" />
      {showText && (
        <span className={`text-xs font-medium ${config.textClass}`}>
          {config.text}
        </span>
      )}
    </div>
  );
};

// ============================================================================
// Exports
// ============================================================================

export { WebSocketContext };
export type { ConnectionStatusIndicatorProps };
